<script setup lang="ts">
import BaseIcon from '@/components/ui/BaseIcon.vue';
import { BOTTOM_SHEET_INTERMEDIATE_RATIO } from '@/components/layout/BottomSheet.vue';
import type { BottomSheetAnchor } from '@/use/bottom-sheet-anchor';
import { computed } from 'vue';

/**
 * The floating "Itinéraire" pill that rides just above a BottomSheet. Place it in
 * the sheet's host (the same offset parent) and feed it the anchor wired to that
 * sheet's `update:height` / `update:intermediateHeight` events.
 */
const props = defineProps<{
  anchor: BottomSheetAnchor;
  /** Defaults to the generic "Itinéraire" label. */
  label?: string;
}>();

defineEmits<{ click: [] }>();

// The ratio only serves until the sheet reports its measured height.
const anchorBottom = computed<string>(() =>
  props.anchor.anchorHeight.value
    ? props.anchor.anchorHeight.value + 'px'
    : BOTTOM_SHEET_INTERMEDIATE_RATIO * 100 + '%',
);
</script>

<template>
  <button
    class="sheet-itinerary-button"
    :class="{ 'sheet-itinerary-button--dragging': anchor.dragging.value }"
    :style="{ '--sheet-anchor': anchorBottom }"
    @click="$emit('click')"
  >
    <BaseIcon name="circle-arrow-right" />
    {{ label ?? $t('nav.itinerary') }}
  </button>
</template>

<style lang="scss">
.sheet-itinerary-button {
  align-items: center;
  background-color: $color-primary-darkest;
  border: none;
  border-radius: $radius-pill;
  // Follows the sheet up to its intermediate snap; the transition matches the
  // sheet's own snap animation and is dropped while a finger drives the sheet.
  bottom: calc(var(--sheet-anchor) + 12px);
  color: $color-white;
  cursor: pointer;
  display: flex;
  font: $label-medium;
  gap: $spacing-2;
  letter-spacing: $label-medium-spacing;
  padding: 14px $spacing-4;
  pointer-events: auto;
  position: absolute;
  right: 16px;
  transition: bottom 0.3s ease;
  z-index: 1;

  &--dragging {
    transition: none;
  }

  .base-icon {
    font-size: 20px;
  }
}
</style>
